/**
 * @file CompanyAnalyticsPage.tsx
 * @brief Company Admin → Analytics dashboard.
 *
 * @details
 * The company-scoped twin of the super-admin Analytics page. A company admin
 * is both an individual user *and* the steward of their whole company, so the
 * page exposes a scope picker — rendered as **"Me" vs "All employees"**, which
 * is what it selects. (The wording matters: "Personal vs Corporate" reads as a
 * split by *payer*, and it is not one — an employee's company-billed sessions
 * appear under "Me" too. The payer is the per-row Corporate badge. The mobile
 * Usage dashboard's toggle was relabelled to match on 24-08-2026.)
 *   • Me            → only the signed-in admin's own sessions / spend
 *   • All employees → the whole company (every employee's sessions / spend)
 *
 * Layout uses GridBlock section cells flush to the content rail.
 *
 * @dependencies
 * - features/company-analytics/api
 * - features/analytics/components/*
 *
 * @references
 * - BACKEND/src/features/company/routes.js (/api/company/analytics/*)
 *
 * @author DES
 * @date 09-07-2026
 * @updated 29-07-2026 — section grid layout
 */

import { useMemo, useState } from "react";
import {
  Activity,
  DollarSign,
  Languages,
  Users,
} from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { useEmployeesList } from "@/features/employees/api";
import { useAuth } from "@/shared/auth/auth-context";
import {
  AnalyticsViewPicker,
  VIEW_ALL,
  VIEW_ME,
} from "./components/AnalyticsViewPicker";
import {
  formatDurationSeconds,
  formatLanguagePair,
  formatMoneyMinor,
} from "@/shared/utils/formatters";
import {
  useCompanyLanguageStats,
  useCompanyRevenueStats,
  useCompanyRevenueTrend,
  useCompanySessionStats,
  useCompanySessionTrend,
  useCompanySessionsPerUser,
  type AnalyticsScope,
} from "./api";
import {
  AnalyticsRangeBar,
  type AnalyticsRange as RangeBarValue,
} from "../analytics/components/AnalyticsRangeBar";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "../analytics/components/GridBlock";
import { gridPageBleedClass } from "../analytics/components/gridPageBleed";
import { StatCard } from "../analytics/components/StatCard";
import { TrendChart } from "../analytics/components/TrendChart";
import { SkeletonRows } from "@/components/ui/Skeleton";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day",
  };
}

export function CompanyAnalyticsPage() {
  const [view, setView] = useState<string>(VIEW_ALL);
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());

  const { user } = useAuth();
  const employeesQuery = useEmployeesList();
  const employees = employeesQuery.data ?? [];
  const otherEmployees = employees.filter((e) => e.id !== user?.id);

  const isCompanyWide = view === VIEW_ALL;
  const scope: AnalyticsScope = view === VIEW_ME ? "personal" : "company";
  const employeeId = view === VIEW_ALL || view === VIEW_ME ? undefined : view;
  const selectedEmployee = employees.find((e) => e.id === view);
  const selectedEmployeeName = selectedEmployee
    ? [selectedEmployee.first_name, selectedEmployee.last_name]
        .filter(Boolean)
        .join(" ")
        .trim() || selectedEmployee.email
    : "";

  const baseRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      scope,
      employeeId,
    }),
    [range.startDate, range.endDate, scope, employeeId],
  );
  const trendRange = useMemo(
    () => ({ ...baseRange, groupBy: range.groupBy }),
    [baseRange, range.groupBy],
  );

  const sessionStats = useCompanySessionStats(baseRange);
  const revenueStats = useCompanyRevenueStats({ ...baseRange });
  const sessionTrend = useCompanySessionTrend(trendRange);
  const revenueTrend = useCompanyRevenueTrend(trendRange);
  // (24-08-2026) Both feeds are per currency now. A company normally bills in
  // one, but the KPI and the chart must agree on which, and neither may print a
  // hard-coded "GBP" over a company that bills in something else.
  const revenueCurrency = revenueStats.data?.currency ?? "GBP";
  const revenueSeries = useMemo(
    () =>
      (revenueTrend.data ?? []).filter(
        (row) => (row.currency ?? revenueCurrency) === revenueCurrency,
      ),
    [revenueTrend.data, revenueCurrency],
  );
  const languages = useCompanyLanguageStats({ ...baseRange, limit: 10 });
  const perUser = useCompanySessionsPerUser(
    { ...baseRange, limit: 10 },
    { enabled: isCompanyWide },
  );

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Analytics</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              {isCompanyWide
                ? "Sessions, spend, and languages across your whole company."
                : view === VIEW_ME
                  ? "Your own sessions, spend, and languages."
                  : `Sessions, spend, and languages for ${
                      selectedEmployeeName || "this employee"
                    }.`}
            </p>
          </div>
          <AnalyticsViewPicker
            value={view}
            onChange={setView}
            employees={otherEmployees}
            loading={employeesQuery.isLoading}
          />
        </GridBand>

        <AnalyticsRangeBar value={range} onChange={setRange} />

        <GridCells className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Sessions"
              icon={Activity}
              tone="brand"
              value={Number(
                sessionStats.data?.total_sessions ?? 0,
              ).toLocaleString()}
              hint={
                sessionStats.data
                  ? `Avg ${formatDurationSeconds(sessionStats.data.avg_duration_seconds)} · Total ${formatDurationSeconds(sessionStats.data.total_duration_seconds)}`
                  : "—"
              }
              isLoading={sessionStats.isLoading}
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Spend"
              icon={DollarSign}
              tone="success"
              value={formatMoneyMinor(
                revenueStats.data?.total_revenue_cents,
                revenueCurrency,
              )}
              hint={
                revenueStats.data
                  ? `${Number(revenueStats.data.total_invoices ?? 0).toLocaleString()} invoices`
                  : "—"
              }
              isLoading={revenueStats.isLoading}
            />
          </GridCell>
          {/* (24-08-2026) The "Cost" and "Net" cards that stood here were
              Verblizr's AI vendor COGS and its margin on this customer's
              account — `session_tokens.total_cost_cents` and
              `revenue − cost`, hinted "Spend − cost". They are internal P&L,
              not the customer's business, and the endpoint no longer returns
              them (see company/routes.js stripInternalMargin). */}
        </GridCells>

        <GridCells className="grid-cols-1 lg:grid-cols-2">
          <GridCell>
            <TrendChart
              variant="cell"
              title="Sessions trend"
              subtitle={`Sessions per ${range.groupBy}`}
              data={sessionTrend.data ?? []}
              isLoading={sessionTrend.isLoading}
              series={[
                {
                  dataKey: "session_count",
                  label: "Sessions",
                  color: "hsl(var(--brand))",
                },
              ]}
            />
          </GridCell>
          <GridCell>
            <TrendChart
              variant="cell"
              title="Spend trend"
              subtitle={`In ${revenueCurrency}, per ${range.groupBy}`}
              data={revenueSeries}
              isLoading={revenueTrend.isLoading}
              series={[
                {
                  dataKey: "total_revenue_cents",
                  label: "Spend",
                  color: "hsl(var(--success))",
                  format: (v) => formatMoneyMinor(v, revenueCurrency),
                },
              ]}
            />
          </GridCell>
        </GridCells>

        <GridCells
          className={cn("grid-cols-1", isCompanyWide && "lg:grid-cols-2")}
        >
          <GridCell padded={false}>
            <div className="space-y-1 px-4 py-4 md:px-5">
              <h3 className="flex items-center gap-2 text-base font-semibold tracking-tight">
                <Languages className="h-4 w-4 text-muted-foreground" />
                Top language pairs
              </h3>
              <p className="text-sm text-muted-foreground">
                Ordered by session count for the selected range.
              </p>
            </div>
            {languages.isLoading ? (
              <SkeletonRows />
            ) : !languages.data || languages.data.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                No language data for this range yet.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                  <tr className="border-t border-border/60">
                    <th className="px-4 py-2.5 text-left font-medium md:px-5">
                      Pair
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium md:px-5">
                      Sessions
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium md:px-5">
                      Total duration
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {languages.data.map((row, idx) => (
                    <tr
                      key={`${row.source_language}-${row.target_language}-${idx}`}
                      className="border-t border-border/60"
                    >
                      <td className="px-4 py-2.5 md:px-5">
                        {formatLanguagePair(
                          row.source_language,
                          row.target_language,
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums md:px-5">
                        {Number(row.session_count).toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums md:px-5">
                        {formatDurationSeconds(row.total_duration_seconds)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </GridCell>

          {isCompanyWide ? (
            <GridCell padded={false}>
              <div className="space-y-1 px-4 py-4 md:px-5">
                <h3 className="flex items-center gap-2 text-base font-semibold tracking-tight">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Top employees by session volume
                </h3>
                <p className="text-sm text-muted-foreground">
                  Your team, ranked for the selected range. Click a row to drill
                  in.
                </p>
              </div>
              {perUser.isLoading ? (
                <SkeletonRows twoLine />
              ) : !perUser.data || perUser.data.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No employee activity for this range.
                </p>
              ) : (
                <ul className="divide-y divide-border/60 border-t border-border/60">
                  {perUser.data.map((u) => (
                    <li key={u.user_id}>
                      <button
                        type="button"
                        onClick={() =>
                          setView(u.user_id === user?.id ? VIEW_ME : u.user_id)
                        }
                        title="View this employee's analytics"
                        className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-brand-soft/40 md:px-5"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">
                            {[u.first_name, u.last_name]
                              .filter(Boolean)
                              .join(" ") ||
                              u.email ||
                              "—"}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {u.email ?? "—"}
                          </p>
                        </div>
                        <div className="ml-3 text-right">
                          <p className="text-sm font-semibold tabular-nums">
                            {Number(u.session_count).toLocaleString()}
                          </p>
                          <p className="text-xs tabular-nums text-muted-foreground">
                            {formatDurationSeconds(u.total_duration_seconds)}
                          </p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </GridCell>
          ) : null}
        </GridCells>
      </GridBlock>
    </div>
  );
}
